import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { BASE_URL } from '../config';

interface Utente {
  id: number;
  username: string;
  nome: string;
  cognome: string;
  enabled: boolean;
}

interface Permesso {
  id?: number;
  id_utente: number;
  entita: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
}

const entitaList = [
  { value: 'risorse', label: 'Risorse' },
  { value: 'utenti', label: 'Utenti' },
  { value: 'progetti', label: 'Progetti' },
  { value: 'strutture', label: 'Strutture' },
  { value: 'utenti_strutture', label: 'Associazioni Utenti/Strutture' },
];

const PermessiPage: React.FC = () => {
  const [utenti, setUtenti] = useState<Utente[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [permessi, setPermessi] = useState<Permesso[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadUtenti();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      loadPermessi(selectedUserId as number);
    } else {
      setPermessi([]);
    }
  }, [selectedUserId]);

  const loadUtenti = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/utenti`, { headers });
      if (!response.ok) {
        throw new Error('Errore nel caricamento utenti');
      }
      const data = await response.json();
      setUtenti(Array.isArray(data) ? data.filter((u: Utente) => u.enabled) : []);
    } catch (err) {
      console.error('Error loading utenti:', err);
      setError('Errore nel caricamento degli utenti');
      setUtenti([]);
    }
  };

  const loadPermessi = async (id_utente: number) => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/permessi/${id_utente}`, { headers });
      if (!response.ok) {
        throw new Error('Errore nel caricamento permessi');
      }
      const data = await response.json();

      // Se non ci sono permessi, inizializza con default (solo lettura)
      if (data.length === 0) {
        const defaultPermessi: Permesso[] = entitaList.map(e => ({
          id_utente,
          entita: e.value,
          can_create: false,
          can_read: true,
          can_update: false,
          can_delete: false,
        }));
        setPermessi(defaultPermessi);
      } else {
        // Assicurati che tutte le entità siano presenti
        const permessiMap = new Map<string, Permesso>(data.map((p: Permesso) => [p.entita, p]));
        const allPermessi: Permesso[] = entitaList.map(e => {
          const existingPermesso = permessiMap.get(e.value);
          if (existingPermesso) {
            return existingPermesso;
          } else {
            return {
              id_utente,
              entita: e.value,
              can_create: false,
              can_read: true,
              can_update: false,
              can_delete: false,
            } as Permesso;
          }
        });
        setPermessi(allPermessi);
      }
    } catch (err) {
      console.error('Error loading permessi:', err);
      setError('Errore nel caricamento dei permessi');
    }
  };

  const handlePermissionChange = (entita: string, field: keyof Permesso, value: boolean) => {
    setPermessi(prev =>
      prev.map(p =>
        p.entita === entita ? { ...p, [field]: value } : p
      )
    );
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      setError('Seleziona un utente');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      // Salva ogni permesso
      for (const permesso of permessi) {
        await fetch(`${BASE_URL}/permessi`, {
          method: 'POST',
          headers,
          body: JSON.stringify(permesso),
        });
      }

      setSuccess('Permessi salvati con successo!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving permessi:', err);
      setError('Errore nel salvataggio dei permessi');
    }
  };

  const selectedUser = utenti.find(u => u.id === selectedUserId);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestione Permessi Utenti
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Selezione utente */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Seleziona Utente</InputLabel>
          <Select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value as number)}
            label="Seleziona Utente"
          >
            <MenuItem value="">
              <em>-- Seleziona un utente --</em>
            </MenuItem>
            {utenti.map((utente) => (
              <MenuItem key={utente.id} value={utente.id}>
                {utente.nome} {utente.cognome} ({utente.username})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Tabella permessi */}
      {selectedUserId && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                Permessi per: {selectedUser?.nome} {selectedUser?.cognome}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedUser?.username}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
            >
              Salva Permessi
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entità</TableCell>
                  <TableCell align="center">
                    <Chip label="Create" size="small" color="success" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Read" size="small" color="info" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Update" size="small" color="warning" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="Delete" size="small" color="error" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permessi.map((permesso) => {
                  const entitaLabel = entitaList.find(e => e.value === permesso.entita)?.label || permesso.entita;
                  return (
                    <TableRow key={permesso.entita}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {entitaLabel}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={permesso.can_create}
                          onChange={(e) => handlePermissionChange(permesso.entita, 'can_create', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={permesso.can_read}
                          onChange={(e) => handlePermissionChange(permesso.entita, 'can_read', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={permesso.can_update}
                          onChange={(e) => handlePermissionChange(permesso.entita, 'can_update', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={permesso.can_delete}
                          onChange={(e) => handlePermissionChange(permesso.entita, 'can_delete', e.target.checked)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Nota: I permessi vengono applicati immediatamente dopo il salvataggio. L'utente dovrà effettuare nuovamente il login per vedere le modifiche.
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PermessiPage;
